import { useRef, useState } from 'react'

/**
 * The photo / CV upload block from Views/Profile/Form.cshtml.
 *
 * The Razor did NOT use a drop zone here: it rendered a label, a hint, a
 * "Upload File" button calling `triggerFileInput(id)`, a hidden `<input
 * type=file>`, and an `.uploaded-file` panel that appeared once a file was
 * chosen. Same markup and class names, so the transcribed CSS applies.
 *
 * Accept lists are the Razor's exactly: photo `.jpg,.jpeg,.png`, CV `.pdf`
 * only — the CV really is PDF-only on this page, even though the Gemini
 * endpoint also accepts DOCX.
 */
export interface UploadFieldProps {
  id: string
  label: string
  required?: boolean
  /** Small grey text after the label, e.g. "(File accepted: jpg, jpeg, png)". */
  accepted?: string
  /** Muted hint rendered on its own line above the button. */
  hint?: string
  buttonIcon: string
  accept: string
  maxBytes: number
  file: File | null
  onFileChange: (file: File | null) => void
  /** Name of a file already stored upstream, shown when nothing new is attached. */
  existingName?: string | null
  successText: string
  invalid?: boolean
}

export function UploadField({
  id,
  label,
  required = false,
  accepted,
  hint,
  buttonIcon,
  accept,
  maxBytes,
  file,
  onFileChange,
  existingName,
  successText,
  invalid = false,
}: UploadFieldProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState('')

  function matches(candidate: File): boolean {
    return accept.split(',').some((rule) => {
      const token = rule.trim().toLowerCase()
      if (!token) return false
      if (token.startsWith('.')) return candidate.name.toLowerCase().endsWith(token)
      if (token.endsWith('/*')) return candidate.type.startsWith(token.slice(0, -1))
      return candidate.type.toLowerCase() === token
    })
  }

  function handle(candidate: File | null): void {
    setError('')
    if (!candidate) {
      onFileChange(null)
      return
    }
    if (!matches(candidate)) {
      setError(`Invalid file type. Accepted: ${accept}`)
      return
    }
    if (candidate.size > maxBytes) {
      setError(`File is too large. Maximum ${formatFileSize(maxBytes)}.`)
      return
    }
    onFileChange(candidate)
  }

  const shownName = file?.name ?? existingName ?? ''

  return (
    <div className="upload-section">
      <label className="upload-label">
        {label} {required && <span className="required">*</span>}
      </label>
      {accepted && <span className="file-accepted">{accepted}</span>}
      {hint && (
        <div className="mb-2">
          <small className="text-muted">{hint}</small>
        </div>
      )}

      <div style={{ marginTop: 12 }}>
        <button
          type="button"
          className={`btn btn-primary${invalid ? ' border border-danger' : ''}`}
          onClick={() => inputRef.current?.click()}
        >
          <i className={buttonIcon} /> Upload File
        </button>
      </div>

      <input
        ref={inputRef}
        type="file"
        id={id}
        accept={accept}
        onChange={(event) => handle(event.target.files?.[0] ?? null)}
      />

      {shownName && (
        <div className="uploaded-file">
          <span className="file-name">{shownName}</span>
          {file && <span className="file-size">{formatFileSize(file.size)}</span>}
          <div className="file-success">
            <i className="fas fa-check-circle" /> {file ? successText : 'Previously uploaded'}
          </div>
          <span
            className="remove-file"
            role="button"
            tabIndex={0}
            title="Remove"
            onClick={() => {
              handle(null)
              if (inputRef.current) inputRef.current.value = ''
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') handle(null)
            }}
          >
            ×
          </span>
        </div>
      )}

      {error && (
        <div className="alert alert-danger mt-2" role="alert">
          {error}
        </div>
      )}
    </div>
  )
}

/** `ProfileForm.formatFileSize` */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'
  const units = ['Bytes', 'KB', 'MB', 'GB']
  const index = Math.floor(Math.log(bytes) / Math.log(1024))
  return `${Number.parseFloat((bytes / Math.pow(1024, index)).toFixed(2))} ${units[index]}`
}
